import { useState } from 'react'

import {
  useSendBulkApi,
  useSendOneApi,
  useTargetListItemsGetApi,
  useTargetListsGetApi
} from '@/common/api/modules/w2Integration'
import { useSiteId } from '@/common/app/site'
import { useShowSavedToast } from '@/common/app/toast'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Selector from '@/components/view/atoms/selector/Selector'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import DetailPageLoader from '@/components/container/DetailPageLoader'

const statusView = (s: string) => {
  if (s === 'sent') return <Text color={TextColor.SUCCESS}>送信済み</Text>
  if (s === 'error') return <Text color={TextColor.CRITICAL}>エラー</Text>
  return <Text color={TextColor.SECONDARY}>未送信</Text>
}

const TargetListPage = () => {
  const siteId = useSiteId()
  const showSaved = useShowSavedToast({ editing: true })
  const {
    data: lists,
    error,
    isLoading,
    mutate: mutateLists
  } = useTargetListsGetApi({ site_id: siteId })
  const [pickedId, setPickedId] = useState<number | null>(null)
  // 選択中リスト: 明示選択が無ければ先頭を採用（effect不要）
  const tlId = pickedId ?? (lists && lists.length > 0 ? lists[0].id : null)
  const setTlId = (id: number) => setPickedId(id)

  const { data: items, mutate: mutateItems } = useTargetListItemsGetApi(
    tlId != null ? { site_id: siteId, tl_id: tlId } : undefined
  )

  const refresh = () => {
    mutateItems()
    mutateLists()
  }
  const { trigger: sendOne } = useSendOneApi({
    onSuccess: () => {
      showSaved('送信')
      refresh()
    }
  })
  const { trigger: sendBulk } = useSendBulkApi({
    onSuccess: () => {
      showSaved('一括送信')
      refresh()
    }
  })
  // どのボタンを実行中か（'item-<id>' / 'bulk-unsent' / 'bulk-all'）。個別にローディング表示する。
  const [sending, setSending] = useState<string | null>(null)
  const anySending = sending !== null

  const selected = lists?.find((l) => l.id === tlId)

  const doBulk = async (scope: 'unsent' | 'all') => {
    if (tlId == null) return
    setSending(`bulk-${scope}`)
    try {
      await sendBulk({ site_id: siteId, tl_id: tlId, scope })
    } finally {
      setSending(null)
    }
  }
  const doOne = async (itemId: number) => {
    setSending(`item-${itemId}`)
    try {
      await sendOne({ site_id: siteId, item_id: itemId })
    } finally {
      setSending(null)
    }
  }

  return (
    <Main type={MainLayoutType.MEDIUM}>
      <PageTitle
        title='ターゲットリスト'
        description='W2Commerce へ送信するターゲットリストの対象（userId）を一覧・送信します。'
      />
      <Content>
        <DetailPageLoader error={error} data={!isLoading && lists}>
          {lists && (
            <Area>
              <Heading>ターゲットリスト</Heading>
              <AreaContent>
                {lists.length === 0 ? (
                  <Text color={TextColor.SECONDARY}>ターゲットリストがありません。</Text>
                ) : (
                  <>
                    <div className='flex flex-wrap items-center gap-3'>
                      <Selector
                        name='tl'
                        choices={lists.map((l) => ({
                          value: l.id,
                          name: `${l.target_name}（${l.total}件）`
                        }))}
                        value={tlId ?? lists[0].id}
                        onChange={(e) => setTlId(Number(e.target.value))}
                      />
                      {selected && (
                        <Text size={12} color={TextColor.SECONDARY}>
                          W2ID: {selected.target_list_id} ／ 未送信 {selected.unsent} ・ 送信済み{' '}
                          {selected.sent} ・ エラー {selected.error}
                        </Text>
                      )}
                      <div className='ml-auto flex gap-2'>
                        <Button
                          color={ButtonColor.PRIMARY}
                          onClick={() => doBulk('unsent')}
                          loading={sending === 'bulk-unsent'}
                          disabled={!selected || selected.unsent === 0 || anySending}
                        >
                          未送信をまとめて送信
                        </Button>
                        <Button
                          color={ButtonColor.SECONDARY}
                          onClick={() => doBulk('all')}
                          loading={sending === 'bulk-all'}
                          disabled={!selected || anySending}
                        >
                          全件送信
                        </Button>
                      </div>
                    </div>

                    <div className='mt-3'>
                      <Table>
                        <thead>
                          <TableRow head>
                            <TableHeadItem>userId</TableHeadItem>
                            <TableHeadItem>状態</TableHeadItem>
                            <TableHeadItem>エラー内容</TableHeadItem>
                            <TableHeadItem>送信日時</TableHeadItem>
                            <TableHeadItem>操作</TableHeadItem>
                          </TableRow>
                        </thead>
                        <tbody>
                          {(items ?? []).map((it) => (
                            <TableRow key={it.id}>
                              <TableRowItem>{it.user_id}</TableRowItem>
                              <TableRowItem>{statusView(it.send_status)}</TableRowItem>
                              <TableRowItem width={320} align='top'>
                                {/* 長いエラー文（URL/HTML等）は固定幅内で折り返す */}
                                <div className='break-all'>
                                  <Text size={12} color={TextColor.SECONDARY} preWrap>
                                    {it.error_message ?? '-'}
                                  </Text>
                                </div>
                              </TableRowItem>
                              <TableRowItem align='top'>
                                {it.sent_at ? it.sent_at.slice(0, 16).replace('T', ' ') : '-'}
                              </TableRowItem>
                              <TableRowItem width={88} align='top'>
                                <Button
                                  color={ButtonColor.PRIMARY}
                                  onClick={() => doOne(it.id)}
                                  loading={sending === `item-${it.id}`}
                                  disabled={anySending}
                                >
                                  送信
                                </Button>
                              </TableRowItem>
                            </TableRow>
                          ))}
                          {items && items.length === 0 && (
                            <TableRow>
                              <TableRowItem>
                                <Text color={TextColor.SECONDARY}>対象データがありません。</Text>
                              </TableRowItem>
                            </TableRow>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </>
                )}
              </AreaContent>
            </Area>
          )}
        </DetailPageLoader>
      </Content>
    </Main>
  )
}

export default TargetListPage
